/**
 * PointsRepository - Database operations for points collections
 * Manages denormalized points across students, classes, and houses
 */

import { BaseRepository, where } from '../../discipline/repositories/BaseRepository';
import { serverTimestamp, runTransaction, doc, getDoc, increment } from 'firebase/firestore';
import { db } from '../../../firebase';

/**
 * Get current ISO week key (e.g., "2026-W09")
 * Used for weekly period tracking on house points.
 */
function getCurrentWeekKey() {
  const now = new Date();
  const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

export class PointsRepository extends BaseRepository {
  constructor() {
    // Use a placeholder collection name - we'll override methods to use specific collections
    super('studentPoints');
  }

  /**
   * Override queryDocuments to use specific collection names
   * @param {Array} constraints - Query constraints
   * @param {string} collectionType - 'students', 'classes', or 'houses'
   * @returns {Promise<Array>} Array of documents
   */
  async queryDocuments(constraints = [], collectionType = 'students') {
    const collectionMap = {
      'students': 'studentPoints',
      'classes': 'classPoints',
      'houses': 'housePoints'
    };

    const collectionName = collectionMap[collectionType] || collectionType;

    // Temporarily override collection name
    const originalCollection = this.collectionName;
    this.collectionName = collectionName;

    try {
      const result = await super.queryDocuments(constraints);
      return result;
    } finally {
      // Restore original collection name
      this.collectionName = originalCollection;
    }
  }

  /**
   * Atomically update points for student, class, and house
   * @param {Object} options - Points update options
   * @returns {Promise<void>}
   */
  async updatePoints({
    studentId,
    studentName,
    classId,
    className,
    houseId,
    houseName,
    amount,
    type = 'recognition', // 'recognition' or 'violation'
    valueId = null,
    valueName = null,
    schoolId
  }) {
    // Validate required parameters
    if (!schoolId) {
      console.error('[PointsRepository] schoolId is required but was not provided', {
        studentId,
        studentName,
        classId,
        className
      });
      throw new Error('schoolId is required for updatePoints operation');
    }

    if (!studentId || !studentName) {
      console.error('[PointsRepository] studentId and studentName are required', {
        studentId,
        studentName,
        schoolId
      });
      throw new Error('studentId and studentName are required for updatePoints operation');
    }

    return runTransaction(db, async (transaction) => {
      // Define document references using top-level collections
      // Path pattern: studentPoints/{studentId}, classPoints/{classId}, housePoints/{houseId}
      const studentRef = doc(db, 'studentPoints', studentId);
      const classRef = classId ? doc(db, 'classPoints', classId) : null;
      const houseRef = houseId ? doc(db, 'housePoints', houseId) : null;

      // Get current documents (handle optional refs)
      const [studentDoc, classDoc, houseDoc] = await Promise.all([
        transaction.get(studentRef),
        classRef ? transaction.get(classRef) : Promise.resolve(null),
        houseRef ? transaction.get(houseRef) : Promise.resolve(null)
      ]);

      const isPositive = type === 'recognition';
      const deltaField = isPositive ? 'positive' : 'negative';

      // Update student points
      if (!studentDoc.exists()) {
        // Create new student points document
        transaction.set(studentRef, {
          studentId,
          studentName,
          houseId: houseId || null, // Can be null if student not in house yet
          schoolId,
          total: isPositive ? amount : -amount,
          positive: isPositive ? amount : 0,
          negative: isPositive ? 0 : amount,
          byValue: valueId && valueName ? {
            [valueId]: {
              name: valueName,
              count: 1,
              points: amount
            }
          } : {},
          lastUpdated: serverTimestamp(),
          pendingHouseAssignment: !houseId // Flag for students without houses
        });
      } else {
        const studentData = studentDoc.data();
        const updates = {
          total: increment(isPositive ? amount : -amount),
          [deltaField]: increment(amount),
          lastUpdated: serverTimestamp()
        };

        // Update value breakdown if provided
        if (valueId && valueName) {
          const currentByValue = studentData.byValue || {};
          const currentValueData = currentByValue[valueId] || { name: valueName, count: 0, points: 0 };
          updates[`byValue.${valueId}`] = {
            name: valueName,
            count: currentValueData.count + 1,
            points: currentValueData.points + amount
          };
        }

        transaction.update(studentRef, updates);
      }

      // Update class points (if class exists)
      if (classRef) {
        if (!classDoc || !classDoc.exists()) {
          // Create new class points document
          const houseBreakdown = {};
          if (houseId) {
            houseBreakdown[houseId] = isPositive ? amount : -amount;
          } else {
            houseBreakdown['unassigned'] = isPositive ? amount : -amount;
          }

          transaction.set(classRef, {
            classId,
            className,
            schoolId,
            total: isPositive ? amount : -amount,
            positive: isPositive ? amount : 0,
            negative: isPositive ? 0 : amount,
            houseBreakdown,
            lastUpdated: serverTimestamp()
          });
        } else {
          const classData = classDoc.data();
          const currentHouseBreakdown = classData.houseBreakdown || {};
          const houseKey = houseId || 'unassigned';
          const currentHousePoints = currentHouseBreakdown[houseKey] || 0;

          transaction.update(classRef, {
            total: increment(isPositive ? amount : -amount),
            [deltaField]: increment(amount),
            [`houseBreakdown.${houseKey}`]: currentHousePoints + (isPositive ? amount : -amount),
            lastUpdated: serverTimestamp()
          });
        }
      }

      // Update house points (only if house exists)
      if (houseRef) {
        const weekKey = getCurrentWeekKey();

        if (!houseDoc || !houseDoc.exists()) {
          // Create new house points document with weekly period
          transaction.set(houseRef, {
            houseId,
            houseName,
            schoolId,
            total: isPositive ? amount : -amount,
            positive: isPositive ? amount : 0,
            negative: isPositive ? 0 : amount,
            lastUpdated: serverTimestamp(),
            periods: {
              [weekKey]: {
                total: isPositive ? amount : -amount,
                positive: isPositive ? amount : 0,
                negative: isPositive ? 0 : amount
              }
            }
          });
        } else {
          transaction.update(houseRef, {
            total: increment(isPositive ? amount : -amount),
            [deltaField]: increment(amount),
            lastUpdated: serverTimestamp(),
            // Weekly period tracking (increment creates field if missing)
            [`periods.${weekKey}.total`]: increment(isPositive ? amount : -amount),
            [`periods.${weekKey}.${deltaField}`]: increment(amount)
          });
        }
      }
    });
  }

  /**
   * Reverse a previous violation points deduction (e.g., when mention is deleted or voided)
   * Atomically updates student, class, and house points.
   * @param {Object} options - Same shape as updatePoints
   * @returns {Promise<void>}
   */
  async reverseViolationPoints({
    studentId,
    studentName,
    classId,
    className,
    houseId,
    houseName,
    amount,
    valueId = null,
    valueName = null,
    schoolId
  }) {
    if (!schoolId) {
      throw new Error('schoolId is required for reverseViolationPoints');
    }
    if (!studentId) {
      throw new Error('studentId is required for reverseViolationPoints');
    }

    return runTransaction(db, async (transaction) => {
      const studentRef = doc(db, 'studentPoints', studentId);
      const classRef = classId ? doc(db, 'classPoints', classId) : null;
      const houseRef = houseId ? doc(db, 'housePoints', houseId) : null;

      const [studentDoc, classDoc, houseDoc] = await Promise.all([
        transaction.get(studentRef),
        classRef ? transaction.get(classRef) : Promise.resolve(null),
        houseRef ? transaction.get(houseRef) : Promise.resolve(null)
      ]);

      // Reverse student points: total += amount, negative -= amount
      if (studentDoc.exists()) {
        const updates = {
          total: increment(amount),
          negative: increment(-amount),
          lastUpdated: serverTimestamp()
        };

        // Reverse value breakdown if provided
        if (valueId) {
          const studentData = studentDoc.data();
          const currentByValue = studentData.byValue || {};
          const currentValueData = currentByValue[valueId];
          if (currentValueData) {
            updates[`byValue.${valueId}`] = {
              name: currentValueData.name || valueName || 'Unknown',
              count: Math.max(0, (currentValueData.count || 0) - 1),
              points: Math.max(0, (currentValueData.points || 0) - amount)
            };
          }
        }

        transaction.update(studentRef, updates);
      }

      // Reverse class points
      if (classRef && classDoc && classDoc.exists()) {
        const classData = classDoc.data();
        const houseKey = houseId || 'unassigned';
        const currentHousePoints = (classData.houseBreakdown || {})[houseKey] || 0;

        transaction.update(classRef, {
          total: increment(amount),
          negative: increment(-amount),
          [`houseBreakdown.${houseKey}`]: currentHousePoints + amount,
          lastUpdated: serverTimestamp()
        });
      }

      // Reverse house points
      if (houseRef && houseDoc && houseDoc.exists()) {
        const weekKey = getCurrentWeekKey();
        transaction.update(houseRef, {
          total: increment(amount),
          negative: increment(-amount),
          lastUpdated: serverTimestamp(),
          [`periods.${weekKey}.total`]: increment(amount),
          [`periods.${weekKey}.negative`]: increment(-amount)
        });
      }
    });
  }

  /**
   * Get points for a student
   * @param {string} studentId - Student ID
   * @returns {Promise<Object|null>} Student points or null
   */
  async getStudentPoints(studentId) {
    const studentRef = doc(db, 'studentPoints', studentId);
    const snapshot = await getDoc(studentRef);
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
  }

  /**
   * Get points for a class
   * @param {string} classId - Class ID
   * @returns {Promise<Object|null>} Class points or null
   */
  async getClassPoints(classId) {
    const classRef = doc(db, 'classPoints', classId);
    const snapshot = await getDoc(classRef);
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
  }

  /**
   * Get points for a house
   * @param {string} houseId - House ID
   * @returns {Promise<Object|null>} House points or null
   */
  async getHousePoints(houseId) {
    const houseRef = doc(db, 'housePoints', houseId);
    const snapshot = await getDoc(houseRef);
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
  }

  /**
   * Get house leaderboard for a school
   * @param {string} schoolId - School ID
   * @returns {Promise<Array>} Array of houses sorted by points
   */
  async getHouseLeaderboard(schoolId) {
    const constraints = [
      where('schoolId', '==', schoolId)
    ];

    const houses = await this.queryDocuments(constraints, 'houses');

    // Sort by total points descending
    houses.sort((a, b) => (b.total || 0) - (a.total || 0));

    return houses;
  }

  /**
   * Get class leaderboard for a school
   * @param {string} schoolId - School ID
   * @returns {Promise<Array>} Array of classes sorted by points
   */
  async getClassLeaderboard(schoolId) {
    const constraints = [
      where('schoolId', '==', schoolId)
    ];

    const classes = await this.queryDocuments(constraints, 'classes');

    // Sort by total points descending
    classes.sort((a, b) => (b.total || 0) - (a.total || 0));

    return classes;
  }

  /**
   * Get student leaderboard for a school or class
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of students sorted by points
   */
  async getStudentLeaderboard(options = {}) {
    const constraints = [];

    if (options.schoolId) {
      constraints.push(where('schoolId', '==', options.schoolId));
    }

    if (options.houseId) {
      constraints.push(where('houseId', '==', options.houseId));
    }

    if (options.classId) {
      // For class-specific leaderboard, we need to fetch from recognition records
      // and aggregate client-side since students don't have classId in points collection
      // This is a limitation we might want to address later
      console.warn('Class-specific student leaderboard requires different approach');
    }

    const students = await this.queryDocuments(constraints, 'students');

    // Sort by total points descending
    students.sort((a, b) => (b.total || 0) - (a.total || 0));

    // Apply limit if specified
    if (options.limit) {
      return students.slice(0, options.limit);
    }

    return students;
  }

  /**
   * Reset points for a specific period
   * @param {string} schoolId - School ID
   * @param {string} period - Period identifier (e.g., 'term1', 'year2024')
   * @param {string} scope - 'all', 'houses', 'classes', or 'students'
   * @returns {Promise<void>}
   */
  async resetPeriod(schoolId, period, scope = 'all') {
    return runTransaction(db, async (transaction) => {
      const resetScopes = scope === 'all'
        ? ['houses', 'classes', 'students']
        : [scope];

      const collectionMap = {
        'students': 'studentPoints',
        'classes': 'classPoints',
        'houses': 'housePoints'
      };

      for (const currentScope of resetScopes) {
        const constraints = [
          where('schoolId', '==', schoolId)
        ];

        const documents = await this.queryDocuments(constraints, currentScope);
        const collectionName = collectionMap[currentScope];

        for (const docData of documents) {
          const docRef = doc(db, collectionName, docData.id);

          // Archive current totals to period snapshot
          const periodSnapshot = {
            total: docData.total || 0,
            positive: docData.positive || 0,
            negative: docData.negative || 0
          };

          // Reset totals and add period snapshot
          transaction.update(docRef, {
            total: 0,
            positive: 0,
            negative: 0,
            [`periods.${period}`]: periodSnapshot,
            lastReset: serverTimestamp(),
            lastUpdated: serverTimestamp()
          });
        }
      }
    });
  }

  /**
   * Recalculate points from source documents (for reconciliation)
   * @param {string} schoolId - School ID
   * @param {string} scope - 'student', 'class', or 'house'
   * @param {string} scopeId - The ID of the entity to recalculate
   * @returns {Promise<Object>} Recalculated totals
   */
  async recalculatePoints(schoolId, scope, scopeId) {
    // This would fetch all recognitions and mentions for the scope
    // and recalculate from scratch - useful for fixing discrepancies
    console.log('Recalculation would be implemented by fetching source documents');

    // Placeholder - in production, this would:
    // 1. Fetch all recognitions for the scope
    // 2. Fetch all mentions (if violations deduct points)
    // 3. Sum up all points
    // 4. Update the points document with correct totals

    return {
      total: 0,
      positive: 0,
      negative: 0,
      message: 'Recalculation not yet implemented'
    };
  }
}